/**
 * Basket payoff at expiry — max profit / max loss / breakevens / curve geometry.
 * Brief #3 of the Basket workstream. Pure: explicit inputs, no DOM, no clock,
 * no feed. Follows live/strike_selection.ts — the maths is testable, live.html
 * only renders what comes out.
 *
 * ANALYSIS ONLY. Nothing here can reach /api/live/order; execution is brief #5.
 *
 * ── One sign convention, not two ──────────────────────────────────────────
 * Payoff at expiry is just P&L against the leg's entry where the "current
 * price" is the option's INTRINSIC value at that underlying price. So every
 * number below goes through `computePnl` — the same function the positions
 * table and `legNotionalPnl` use — rather than restating BUY/SELL algebra here.
 * A second convention that disagrees by a sign is worse than no feature.
 *
 * ── Why there is no sampling ──────────────────────────────────────────────
 * Basket payoff is PIECEWISE LINEAR with kinks only at strikes. That is not a
 * convenience, it is what makes the answers exact:
 *
 *   * extrema occur at a vertex (S=0 or a strike) or at infinity — so scanning
 *     a price grid can only ever be less accurate, and
 *   * breakevens are roots of a straight line on each segment — solved, not
 *     hunted for. A grid scan misses roots between samples and invents them on
 *     a segment that sits flat at zero.
 *
 * ── D3, the one that matters ──────────────────────────────────────────────
 * Unboundedness is derived from the NET SLOPE beyond the outermost strike,
 * never from the max/min of a plotted range. Taking extrema off a sampled range
 * reports a large finite number for a naked short call — wrong, and wrong in
 * the direction that makes a dangerous position look bounded. Note the
 * asymmetry: S can rise without limit but floors at 0, so a "unlimited
 * downside" position is really bounded by its value at S=0, and we report that
 * real, finite number instead of crying unlimited.
 *
 * All figures are NOTIONAL and GROSS of charges (D6), matching brief #2's
 * basketNotionalPnl. charges.ts exists if net is ever wanted; the label must
 * then change with it.
 */

import { computePnl } from "../paper/positions";
import { legQty, type BasketLeg } from "./leg";

/** A bound that may genuinely not exist. Never collapse `unlimited` to a number. */
export type Bound =
  | { kind: "finite"; value: number }
  | { kind: "unlimited" };

export const UNLIMITED: Bound = { kind: "unlimited" };

export type ExclusionReason =
  /** D4 — no entry price captured at add, so no cost basis and no curve. */
  | "no-entry"
  /** D2 — expires after the modelled expiry; still open, so not modelled. */
  | "later-expiry";

export interface ExcludedLeg {
  leg: BasketLeg;
  reason: ExclusionReason;
}

/** A stretch where payoff sits exactly at zero — a breakeven RANGE, not a root. */
export interface BreakevenRange {
  from: number;
  /** `null` = the flat-zero stretch continues to infinity. */
  to: number | null;
}

export interface UnderlyingPayoff {
  underlying: string;
  exchange: string;
  /** The expiry modelled — the NEAREST one present (D2). */
  expiry: string;
  /** Legs actually in the curve. */
  legs: BasketLeg[];
  /** Legs left out, with the reason. Must be shown, never silently dropped. */
  excluded: ExcludedLeg[];
  /** Distinct strikes, ascending — the kinks. */
  strikes: number[];
  /** Highest payoff over S ∈ [0, ∞). */
  maxProfit: Bound;
  /** Lowest payoff over S ∈ [0, ∞). NEGATIVE when finite — it is a P&L, not a magnitude. */
  maxLoss: Bound;
  /** Exact roots, ascending. */
  breakevens: number[];
  /** Flat-at-zero stretches, which are not roots. */
  breakevenRanges: BreakevenRange[];
  /** Net premium from ENTRY prices (D5): positive = credit received, negative = debit paid. */
  netPremium: number;
  /** dPayoff/dS above the highest strike. Non-zero ⇒ unbounded that way (D3). */
  slopeAbove: number;
  /** dPayoff/dS below the lowest strike. Informational — S floors at 0. */
  slopeBelow: number;
  /** Payoff at S=0, the true worst case when slopeBelow < 0. */
  payoffAtZero: number;
}

export interface BasketPayoff {
  /** One analysis per underlying (D1) — a payoff diagram has ONE price axis. */
  perUnderlying: UnderlyingPayoff[];
  /** True when the basket spans more than one underlying. */
  mixedUnderlyings: boolean;
  /**
   * Summed max profit / max loss across underlyings — ONLY when every group's
   * bound is finite (D1/D3). `null` when it would not be honestly additive, in
   * which case the UI must show the per-underlying figures and no total.
   */
  combined: { maxProfit: Bound; maxLoss: Bound } | null;
  /** D6 — these are notional and gross of charges. */
  gross: true;
}

// ── per-leg primitives ───────────────────────────────────────────────────────

/** Option value at expiry: all time value is gone, only intrinsic remains. */
export function intrinsicValue(leg: BasketLeg, s: number): number {
  const k = Number(leg.strike);
  return leg.optType === "CE" ? Math.max(s - k, 0) : Math.max(k - s, 0);
}

/**
 * One leg's P&L at expiry with the underlying at `s`.
 *
 * Deliberately `computePnl(..., intrinsicValue(...))` and not a restatement of
 * the four long/short call/put formulas: it is the same operation, and writing
 * it twice is how the two drift apart.
 */
export function legPayoffAt(leg: BasketLeg, s: number): number {
  if (leg == null || leg.entry == null) return 0;
  return computePnl(
    { side: leg.side, avg: Number(leg.entry), qty: legQty(leg) },
    intrinsicValue(leg, s),
  );
}

/** Basket P&L at expiry with the underlying at `s`. Legs must be pre-filtered. */
export function payoffAt(legs: BasketLeg[], s: number): number {
  let total = 0;
  for (const leg of legs || []) total += legPayoffAt(leg, s);
  return total;
}

/**
 * dPayoff/dS above the highest strike: every call is in the money and moves
 * 1:1 with S, every put is worthless and flat.
 *
 * (A futures/underlying leg would add ±qty to BOTH slopes. BasketLeg has no
 * such type today — optType is "CE" | "PE" — so the sum is over calls alone.
 * If futures legs are ever added, they belong in both this and slopeBelow.)
 */
export function slopeAbove(legs: BasketLeg[]): number {
  let slope = 0;
  for (const leg of legs || []) {
    if (leg.optType !== "CE") continue;
    slope += (leg.side === "BUY" ? 1 : -1) * legQty(leg);
  }
  return slope;
}

/** dPayoff/dS below the lowest strike: puts are ITM and move -1:1 with S. */
export function slopeBelow(legs: BasketLeg[]): number {
  let slope = 0;
  for (const leg of legs || []) {
    if (leg.optType !== "PE") continue;
    slope += (leg.side === "BUY" ? -1 : 1) * legQty(leg);
  }
  return slope;
}

/** Net premium from ENTRY prices (D5). Positive = credit taken in. */
export function netPremium(legs: BasketLeg[]): number {
  let net = 0;
  for (const leg of legs || []) {
    if (leg.entry == null) continue;
    net += (leg.side === "SELL" ? 1 : -1) * Number(leg.entry) * legQty(leg);
  }
  return net;
}

/** Distinct strikes, ascending — the only points where the curve bends. */
export function sortedStrikes(legs: BasketLeg[]): number[] {
  return [...new Set((legs || []).map(l => Number(l.strike)).filter(k => isFinite(k)))]
    .sort((a, b) => a - b);
}

// ── exact extrema and roots ──────────────────────────────────────────────────

/** Tolerance for "this value is zero". Payoffs are rupee amounts × lot sizes. */
const EPS = 1e-9;

/**
 * Vertices of the piecewise-linear payoff over [0, ∞): S=0 plus every strike.
 * The extremum of a piecewise-linear function is attained at a vertex or at
 * infinity, so these plus the two slopes describe the function completely.
 */
function vertices(strikes: number[]): number[] {
  return strikes.length && strikes[0] === 0 ? strikes.slice() : [0, ...strikes];
}

/**
 * Exact max/min over S ∈ [0, ∞).
 *
 * D3 lives here. Rising S is unbounded, so a non-zero `slopeAbove` sends the
 * payoff to ±∞ and the corresponding bound is `unlimited`. Falling S is NOT
 * unbounded — it stops at zero — so however steep `slopeBelow` is, the worst
 * case is the finite value at S=0 and we report that number.
 */
export function extrema(legs: BasketLeg[]): { maxProfit: Bound; maxLoss: Bound } {
  const strikes = sortedStrikes(legs);
  const vs = vertices(strikes);
  const values = vs.map(s => payoffAt(legs, s));
  const up = slopeAbove(legs);
  const finiteMax = values.length ? Math.max(...values) : 0;
  const finiteMin = values.length ? Math.min(...values) : 0;
  return {
    maxProfit: up > EPS ? UNLIMITED : { kind: "finite", value: finiteMax },
    maxLoss: up < -EPS ? UNLIMITED : { kind: "finite", value: finiteMin },
  };
}

/**
 * Exact breakevens, by solving each linear segment for zero.
 *
 * Segments are [0,k1], [k1,k2], …, [kn, ∞). A sign change across a segment has
 * exactly one root, found by interpolation. A vertex sitting at zero is a root
 * in its own right. A segment flat at zero is a breakeven RANGE — reported
 * separately, because emitting its endpoints as two roots would imply the
 * payoff crosses there when it does not.
 */
export function breakevens(legs: BasketLeg[]): {
  points: number[];
  ranges: BreakevenRange[];
} {
  const strikes = sortedStrikes(legs);
  const vs = vertices(strikes);
  if (!vs.length) return { points: [], ranges: [] };

  const val = (s: number) => payoffAt(legs, s);
  const points: number[] = [];
  const ranges: BreakevenRange[] = [];
  const isZero = (v: number) => Math.abs(v) <= EPS;

  // Finite segments.
  for (let i = 0; i < vs.length - 1; i++) {
    const a = vs[i], b = vs[i + 1];
    const fa = val(a), fb = val(b);
    if (isZero(fa) && isZero(fb)) { ranges.push({ from: a, to: b }); continue; }
    if (isZero(fa)) { points.push(a); continue; }
    if (isZero(fb)) continue;                 // picked up as `fa` of the next segment
    if ((fa < 0) !== (fb < 0)) points.push(a + ((b - a) * -fa) / (fb - fa));
  }

  // The terminal ray [last vertex, ∞).
  const last = vs[vs.length - 1];
  const fLast = val(last);
  const up = slopeAbove(legs);
  if (Math.abs(up) <= EPS) {
    // Flat forever: either identically zero (a range to infinity) or never zero.
    if (isZero(fLast)) ranges.push({ from: last, to: null });
    else if (vs.length === 1 && !isZero(fLast)) { /* no root anywhere */ }
  } else if (isZero(fLast)) {
    points.push(last);
  } else {
    const root = last - fLast / up;
    if (root > last) points.push(root);
  }

  // A vertex can be reached from both sides; keep one of each.
  const uniq = [...new Set(points.map(p => Number(p.toFixed(6))))].sort((a, b) => a - b);
  const covered = (p: number) => ranges.some(r => p >= r.from - EPS && (r.to == null || p <= r.to + EPS));
  return { points: uniq.filter(p => !covered(p)), ranges };
}

// ── grouping: D1 (underlying) and D2 (expiry) ────────────────────────────────

/** Legs of one underlying share a price axis only if they also share an exchange. */
function groupKey(leg: BasketLeg): string {
  return `${leg.exchange}|${leg.underlying}`;
}

/**
 * Analyse the legs of ONE underlying.
 *
 * D2 — "payoff at expiry" is undefined across different expiry dates: a
 * calendar's far leg still has time value that a terminal payoff cannot express
 * without an options model. We model the NEAREST expiry and mark later legs
 * `later-expiry`, EXCLUDED from the curve. Including them at intrinsic value
 * would be treating a live leg as if it had expired, which flatters the P&L —
 * and this is a number people size positions from.
 *
 * D4 — a leg with no entry has no cost basis; excluded and reported, never
 * assumed to be zero.
 */
export function analyseUnderlying(legs: BasketLeg[]): UnderlyingPayoff {
  const all = (legs || []).filter(Boolean);
  const first = all[0];
  const excluded: ExcludedLeg[] = [];

  const expiries = [...new Set(all.map(l => l.expiry).filter(Boolean))].sort();
  const expiry = expiries[0] || (first ? first.expiry : "");

  const included: BasketLeg[] = [];
  for (const leg of all) {
    if (leg.expiry && expiry && leg.expiry !== expiry) {
      excluded.push({ leg, reason: "later-expiry" });
    } else if (leg.entry == null) {
      excluded.push({ leg, reason: "no-entry" });
    } else {
      included.push(leg);
    }
  }

  const { maxProfit, maxLoss } = extrema(included);
  const be = breakevens(included);
  return {
    underlying: first ? first.underlying : "",
    exchange: first ? first.exchange : "",
    expiry,
    legs: included,
    excluded,
    strikes: sortedStrikes(included),
    maxProfit,
    maxLoss,
    breakevens: be.points,
    breakevenRanges: be.ranges,
    netPremium: netPremium(included),
    slopeAbove: slopeAbove(included),
    slopeBelow: slopeBelow(included),
    payoffAtZero: payoffAt(included, 0),
  };
}

/**
 * Analyse a whole basket.
 *
 * D1 — a payoff diagram plots P&L against ONE underlying price axis, and NIFTY
 * at 24,600 says nothing about where SENSEX is. So mixed-underlying baskets are
 * analysed and plotted PER UNDERLYING. The combined total is offered only when
 * every group's max profit and max loss are finite, because summing anything
 * with an `unlimited` in it is meaningless. Neither underlying is dropped and
 * nothing is plotted on a shared axis.
 */
export function analyseBasket(legs: BasketLeg[]): BasketPayoff {
  const groups = new Map<string, BasketLeg[]>();
  for (const leg of (legs || []).filter(Boolean)) {
    const key = groupKey(leg);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(leg);
  }
  const perUnderlying = [...groups.values()].map(analyseUnderlying);

  const allFinite = perUnderlying.length > 0 && perUnderlying.every(
    u => u.maxProfit.kind === "finite" && u.maxLoss.kind === "finite",
  );
  const sum = (pick: (u: UnderlyingPayoff) => Bound) =>
    perUnderlying.reduce((t, u) => t + (pick(u) as { value: number }).value, 0);

  return {
    perUnderlying,
    mixedUnderlyings: perUnderlying.length > 1,
    combined: allFinite
      ? {
        maxProfit: { kind: "finite", value: sum(u => u.maxProfit) },
        maxLoss: { kind: "finite", value: sum(u => u.maxLoss) },
      }
      : null,
    gross: true,
  };
}

// ── graph geometry (still pure — live.html only writes the SVG) ─────────────

export interface Domain { lo: number; hi: number }

/**
 * The price range to plot: at least ±`pct` around spot, widened to contain every
 * strike plus a margin. A diagram that clips a strike hides a kink and is
 * misleading, so the strikes win whenever they are wider.
 */
export function payoffDomain(
  strikes: number[], spot: number | null | undefined, pct = 0.12,
): Domain {
  const ks = (strikes || []).filter(k => isFinite(k)).sort((a, b) => a - b);
  const s = spot != null && isFinite(Number(spot)) && Number(spot) > 0 ? Number(spot) : null;
  const anchors: number[] = [];
  if (s != null) anchors.push(s * (1 - pct), s * (1 + pct));
  if (ks.length) {
    const span = ks[ks.length - 1] - ks[0];
    const pad = Math.max(span * 0.15, (s != null ? s : ks[0]) * 0.04);
    anchors.push(ks[0] - pad, ks[ks.length - 1] + pad);
  }
  if (!anchors.length) return { lo: 0, hi: 1 };
  const lo = Math.max(0, Math.min(...anchors));
  const hi = Math.max(...anchors);
  return hi > lo ? { lo, hi } : { lo, hi: lo + 1 };
}

/**
 * Exact polyline for the curve: the domain ends plus every strike inside it.
 * Because the payoff is linear between strikes, these vertices ARE the curve —
 * denser sampling would add points, never accuracy.
 */
export function payoffCurve(
  legs: BasketLeg[], domain: Domain,
): Array<{ s: number; pnl: number }> {
  const inside = sortedStrikes(legs).filter(k => k > domain.lo && k < domain.hi);
  const xs = [domain.lo, ...inside, domain.hi];
  return xs.map(s => ({ s, pnl: payoffAt(legs, s) }));
}

export interface PlotBox {
  width: number; height: number;
  padLeft: number; padRight: number; padTop: number; padBottom: number;
}

/**
 * Map curve points into SVG user space, with a y-scale that ALWAYS includes
 * zero — the zero line is the reference the whole diagram is read against, so a
 * curve that never crosses it must still be shown in its true position.
 */
export function projectCurve(
  points: Array<{ s: number; pnl: number }>, domain: Domain, box: PlotBox,
): { pts: Array<{ x: number; y: number }>; zeroY: number; x: (s: number) => number; y: (v: number) => number; yMin: number; yMax: number } {
  const x0 = box.padLeft, x1 = box.width - box.padRight;
  const y0 = box.padTop, y1 = box.height - box.padBottom;
  const vals = points.map(p => p.pnl);
  let lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
  if (hi - lo < 1e-6) { lo -= 1; hi += 1; }
  const pad = (hi - lo) * 0.08;
  lo -= pad; hi += pad;
  const x = (s: number) => x0 + ((s - domain.lo) / (domain.hi - domain.lo)) * (x1 - x0);
  const y = (v: number) => y1 - ((v - lo) / (hi - lo)) * (y1 - y0);
  return {
    pts: points.map(p => ({ x: x(p.s), y: y(p.pnl) })),
    zeroY: y(0),
    x,
    y,
    yMin: lo,
    yMax: hi,
  };
}

/** `M x,y L x,y …` for a projected polyline. */
export function toPolyline(pts: Array<{ x: number; y: number }>): string {
  if (!pts.length) return "";
  return pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
}

/** The curve closed down to the zero line, for the two-tone sign fill. */
export function toAreaPath(pts: Array<{ x: number; y: number }>, zeroY: number): string {
  if (!pts.length) return "";
  const first = pts[0], last = pts[pts.length - 1];
  return `${toPolyline(pts)} L${last.x.toFixed(2)},${zeroY.toFixed(2)} L${first.x.toFixed(2)},${zeroY.toFixed(2)} Z`;
}

/** "Unlimited" or a formatted number — the ONLY place a Bound becomes text. */
export function formatBound(b: Bound, fmt: (n: number) => string = n => Math.abs(n).toFixed(2)): string {
  return b.kind === "unlimited" ? "Unlimited" : fmt(b.value);
}


// ── SVG Render Engine & Interactive Simulation ──────────────────────────────

export interface RenderPayoffSvgOpts {
  legs: BasketLeg[];
  spot: number | null | undefined;
  targetSpot?: number | null | undefined;
  box?: PlotBox;
  idPrefix?: string;
}

export interface RenderPayoffSvgResult {
  svgHtml: string;
  domain: Domain;
  currentSpot: number | null;
  targetSpot: number | null;
  targetPnl: number;
  breakevens: number[];
  maxProfit: Bound;
  maxLoss: Bound;
  netPremium: number;
}

export const DEFAULT_PLOT_BOX: PlotBox = {
  width: 580,
  height: 220,
  padLeft: 55,
  padRight: 35,
  padTop: 26,
  padBottom: 28,
};

/**
 * Pure SVG renderer for options strategy payoff at expiry.
 * Renders the two-tone profit/loss area, piecewise linear curve,
 * zero baseline, strike marks, breakevens, spot marker, and interactive
 * target spot scrubber.
 */
export function renderPayoffSvg(opts: RenderPayoffSvgOpts): RenderPayoffSvgResult {
  const box = opts.box || DEFAULT_PLOT_BOX;
  const rawLegs = (opts.legs || []).filter(Boolean).map(leg => {
    const entryVal = leg.entry != null ? leg.entry : ((leg as any).ltp != null ? (leg as any).ltp : (leg as any).price);
    const lotSizeVal = leg.lotSize || ((leg as any).qty && leg.lots ? Math.round((leg as any).qty / leg.lots) : (leg as any).qty) || 1;
    const lotsVal = leg.lots || 1;
    return {
      underlying: leg.underlying || "NIFTY",
      exchange: leg.exchange || "NSE",
      expiry: leg.expiry || "",
      ...leg,
      lots: lotsVal,
      lotSize: lotSizeVal,
      entry: entryVal != null && isFinite(Number(entryVal)) ? Number(entryVal) : leg.entry,
    } as BasketLeg;
  });
  const u = analyseUnderlying(rawLegs);
  const s = opts.spot != null && isFinite(Number(opts.spot)) && Number(opts.spot) > 0 ? Number(opts.spot) : null;
  const dom = payoffDomain(u.strikes, s);

  let targetSpot = opts.targetSpot != null && isFinite(Number(opts.targetSpot)) ? Number(opts.targetSpot) : null;
  if (targetSpot == null && s != null) {
    targetSpot = s;
  }
  const targetPnl = targetSpot != null ? payoffAt(u.legs, targetSpot) : 0;

  if (!u.legs.length) {
    const emptySvg = `<svg viewBox="0 0 ${box.width} ${box.height}" width="100%" height="${box.height}" role="img" aria-label="No option legs">
      <rect width="100%" height="100%" fill="transparent"/>
      <text x="${box.width / 2}" y="${box.height / 2}" fill="var(--muted, #8E95A0)" font-size="12" text-anchor="middle">
        Add option legs with market quotes to display payoff diagram
      </text>
    </svg>`;
    return {
      svgHtml: emptySvg,
      domain: dom,
      currentSpot: s,
      targetSpot,
      targetPnl: 0,
      breakevens: [],
      maxProfit: u.maxProfit,
      maxLoss: u.maxLoss,
      netPremium: u.netPremium,
    };
  }

  const points = payoffCurve(u.legs, dom);
  const proj = projectCurve(points, dom, box);
  const area = toAreaPath(proj.pts, proj.zeroY);
  const line = toPolyline(proj.pts);
  const uid = (opts.idPrefix || "pf") + "_" + Math.random().toString(36).slice(2, 8);

  // Strike vertical dashed guides + labels
  const marks = u.strikes.filter((k) => k > dom.lo && k < dom.hi).map((k) => {
    const xPos = proj.x(k).toFixed(1);
    return `
      <line x1="${xPos}" y1="${box.padTop}" x2="${xPos}" y2="${(box.height - box.padBottom).toFixed(1)}" stroke="var(--muted, #8E95A0)" stroke-width="1" stroke-dasharray="3 3" opacity="0.3"/>
      <text x="${xPos}" y="${(box.padTop + 10).toFixed(1)}" fill="var(--muted, #8E95A0)" font-size="9" text-anchor="middle" font-family="var(--font-mono, monospace)">${k}</text>
    `;
  }).join("");

  // Breakeven vertical solid amber guides + labels
  const bes = u.breakevens.filter((b) => b > dom.lo && b < dom.hi).map((b) => {
    const xPos = proj.x(b).toFixed(1);
    return `
      <line x1="${xPos}" y1="${box.padTop}" x2="${xPos}" y2="${(box.height - box.padBottom).toFixed(1)}" stroke="#F59E0B" stroke-width="1.5" stroke-dasharray="4 2"/>
      <text x="${xPos}" y="${(box.height - 7).toFixed(1)}" fill="#F59E0B" font-size="10" font-weight="700" text-anchor="middle" font-family="var(--font-mono, monospace)">BE ${b.toFixed(0)}</text>
    `;
  }).join("");

  // Live Spot vertical line + label
  const spotMark = (s != null && s > dom.lo && s < dom.hi)
    ? `
      <line x1="${proj.x(s).toFixed(1)}" y1="${box.padTop}" x2="${proj.x(s).toFixed(1)}" y2="${(box.height - box.padBottom).toFixed(1)}" stroke="#38BDF8" stroke-width="1.5" opacity="0.85"/>
      <text x="${proj.x(s).toFixed(1)}" y="${(box.padTop - 8).toFixed(1)}" fill="#38BDF8" font-size="10" font-weight="700" text-anchor="middle" font-family="var(--font-mono, monospace)">Spot ${s.toFixed(0)}</text>
    `
    : "";

  // Interactive Target Spot Scrubber (crosshair cursor on payoff curve)
  let scrubberMark = "";
  if (targetSpot != null && targetSpot >= dom.lo && targetSpot <= dom.hi) {
    const tx = proj.x(targetSpot);
    const ty = proj.y(targetPnl);
    const pnlSign = targetPnl >= 0 ? "+" : "";
    const pnlColor = targetPnl >= 0 ? "#10B981" : "#EF4444";
    const badgeY = Math.max(box.padTop + 14, Math.min(box.height - box.padBottom - 12, ty - 10));

    const badgeText = `${Math.round(targetSpot)} | ${pnlSign}₹${Math.round(targetPnl).toLocaleString("en-IN")}`;
    const badgeWidth = Math.max(110, badgeText.length * 7.5);
    const badgeX = Math.max(box.padLeft + 4, Math.min(box.width - box.padRight - badgeWidth - 4, tx - badgeWidth / 2));

    scrubberMark = `
      <g class="payoff-scrubber" data-target-spot="${targetSpot}" data-target-pnl="${targetPnl}">
        <line x1="${tx.toFixed(1)}" y1="${box.padTop}" x2="${tx.toFixed(1)}" y2="${(box.height - box.padBottom).toFixed(1)}" stroke="#A855F7" stroke-width="1.6" stroke-dasharray="3 3"/>
        <circle cx="${tx.toFixed(1)}" cy="${ty.toFixed(1)}" r="5" fill="${pnlColor}" stroke="#FFFFFF" stroke-width="2"/>
        <rect x="${badgeX.toFixed(1)}" y="${(badgeY - 13).toFixed(1)}" width="${badgeWidth.toFixed(1)}" height="18" rx="4" fill="var(--panel-3, #292D35)" stroke="${pnlColor}" stroke-width="1.2" opacity="0.95"/>
        <text x="${(badgeX + badgeWidth / 2).toFixed(1)}" y="${(badgeY).toFixed(1)}" fill="${pnlColor}" font-size="10" font-weight="800" text-anchor="middle" font-family="var(--font-mono, monospace)">
          ${badgeText}
        </text>
      </g>
    `;
  }

  const svgHtml = `<svg viewBox="0 0 ${box.width} ${box.height}" width="100%" height="${box.height}" role="img" aria-label="Payoff diagram at expiry for ${u.underlying || "strategy"}">
    <defs>
      <clipPath id="${uid}up"><rect x="0" y="0" width="${box.width}" height="${proj.zeroY.toFixed(2)}"/></clipPath>
      <clipPath id="${uid}dn"><rect x="0" y="${proj.zeroY.toFixed(2)}" width="${box.width}" height="${(box.height - proj.zeroY).toFixed(2)}"/></clipPath>
    </defs>
    <!-- Background zero horizontal baseline -->
    <line x1="${box.padLeft}" y1="${proj.zeroY.toFixed(2)}" x2="${box.width - box.padRight}" y2="${proj.zeroY.toFixed(2)}" stroke="var(--border-strong, #383D47)" stroke-width="1.2"/>
    <text x="${box.padLeft - 6}" y="${(proj.zeroY + 3).toFixed(2)}" fill="var(--muted, #8E95A0)" font-size="9.5" text-anchor="end" font-family="var(--font-mono, monospace)">₹0</text>

    <!-- Strike guides -->
    ${marks}

    <!-- Two-tone sign fill -->
    <path d="${area}" fill="rgba(16, 185, 129, 0.22)" clip-path="url(#${uid}up)"/>
    <path d="${area}" fill="rgba(239, 68, 68, 0.22)" clip-path="url(#${uid}dn)"/>

    <!-- Spot and Breakeven markers -->
    ${spotMark}
    ${bes}

    <!-- Payoff polyline -->
    <path d="${line}" fill="none" stroke="var(--text, #ECEEF1)" stroke-width="2.2"/>

    <!-- Target Spot Scrubber cursor -->
    ${scrubberMark}

    <!-- Domain X-axis limits -->
    <text x="${box.padLeft}" y="${box.height - 7}" fill="var(--muted, #8E95A0)" font-size="10" font-family="var(--font-mono, monospace)">${dom.lo.toFixed(0)}</text>
    <text x="${box.width - box.padRight}" y="${box.height - 7}" fill="var(--muted, #8E95A0)" font-size="10" text-anchor="end" font-family="var(--font-mono, monospace)">${dom.hi.toFixed(0)}</text>
  </svg>`;

  return {
    svgHtml,
    domain: dom,
    currentSpot: s,
    targetSpot,
    targetPnl,
    breakevens: u.breakevens,
    maxProfit: u.maxProfit,
    maxLoss: u.maxLoss,
    netPremium: u.netPremium,
  };
}
